// Helpers compartidos para subida de media del Camino.
// Todas las subidas van al backend con folder=camino-2026 → Bunny en camino-2026/{images,audios,videos}.
//
// APIs:
//   uploadImage(file, onProgress?) -> Promise<url>
//   uploadAudio(file, onProgress?) -> Promise<url>
//   uploadVideo(file, onProgress?) -> Promise<url>
//   generateVideoThumbnail(file)   -> Promise<File>  (JPEG del primer frame útil)

import axios from 'axios';

const API_BASE = 'https://api.appagustinos.es/api';
const FOLDER = 'camino-2026';

const authHeader = () => {
  const token = localStorage.getItem('cms_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const attachProgress = (onProgress) => (
  onProgress
    ? {
        onUploadProgress: (e) => {
          if (e.total) onProgress(Math.round((e.loaded / e.total) * 100));
        }
      }
    : {}
);

// Sube una imagen. Devuelve la URL final de Bunny.
export const uploadImage = async (file, onProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('folder', FOLDER);
  const res = await axios.post(`${API_BASE}/upload/image`, formData, {
    headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
    ...attachProgress(onProgress)
  });
  if (!res.data?.success) throw new Error(res.data?.error || 'Error subiendo imagen');
  return res.data.data.imageUrl;
};

// Sube un audio con el flujo token + PUT streaming a Bunny.
export const uploadAudio = async (file, onProgress) => {
  const ext = (file.name.split('.').pop() || 'mp3').toLowerCase();
  const tokenRes = await axios.post(
    `${API_BASE}/upload/audio-token`,
    { ext, folder: FOLDER },
    { headers: authHeader() }
  );
  if (!tokenRes.data?.success) throw new Error(tokenRes.data?.error || 'Error generando token de audio');
  const { token, finalUrl } = tokenRes.data;

  const putRes = await axios.put(
    `${API_BASE}/upload/audio?token=${encodeURIComponent(token)}`,
    file,
    {
      headers: { 'Content-Type': file.type || 'audio/mpeg' },
      ...attachProgress(onProgress)
    }
  );
  if (!putRes.data?.success) throw new Error(putRes.data?.error || 'Error subiendo audio');
  return finalUrl;
};

// Sube un video con el flujo token + PUT streaming a Bunny.
export const uploadVideo = async (file, onProgress) => {
  const ext = (file.name.split('.').pop() || 'mp4').toLowerCase();
  const tokenRes = await axios.post(
    `${API_BASE}/upload/video-token`,
    { ext, folder: FOLDER },
    { headers: authHeader() }
  );
  if (!tokenRes.data?.success) throw new Error(tokenRes.data?.error || 'Error generando token de video');
  const { token, finalUrl } = tokenRes.data;

  const putRes = await axios.put(
    `${API_BASE}/upload/video?token=${encodeURIComponent(token)}`,
    file,
    {
      headers: { 'Content-Type': file.type || 'video/mp4' },
      ...attachProgress(onProgress)
    }
  );
  if (!putRes.data?.success) throw new Error(putRes.data?.error || 'Error subiendo video');
  return finalUrl;
};

// Genera un thumbnail (File JPEG) a partir de un video local.
// Captura el frame a 1.5s (o 15% del video, lo que sea menor). Si el frame
// es muy oscuro, reintenta a la mitad del video.
export const generateVideoThumbnail = (file) => new Promise((resolve, reject) => {
  const video = document.createElement('video');
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  video.preload = 'auto';
  video.muted = true;
  video.playsInline = true;
  video.crossOrigin = 'anonymous';

  let hasAttempted = false;
  let videoUrl = null;

  const cleanup = () => {
    if (videoUrl) {
      URL.revokeObjectURL(videoUrl);
      videoUrl = null;
    }
  };

  video.onloadeddata = () => {
    // Esperar un momento para asegurar que el video esté decodificado.
    setTimeout(() => {
      if (!hasAttempted) {
        hasAttempted = true;
        const captureTime = Math.min(1.5, video.duration * 0.15);
        video.currentTime = captureTime;
      }
    }, 500);
  };

  video.onseeked = () => {
    // Dos requestAnimationFrame para que el frame se pinte.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;

          if (canvas.width === 0 || canvas.height === 0) {
            throw new Error('Dimensiones del video inválidas');
          }

          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          // Verificar brillo promedio (muestreo cada 100 px). Si es muy oscuro,
          // reintentar en la mitad del video una sola vez.
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          let sumBrightness = 0;
          for (let i = 0; i < data.length; i += 400) {
            sumBrightness += (data[i] + data[i + 1] + data[i + 2]) / 3;
          }
          const avgBrightness = sumBrightness / (data.length / 400);

          if (avgBrightness < 10 && video.currentTime < video.duration * 0.4) {
            video.currentTime = video.duration * 0.5;
            return;
          }

          canvas.toBlob((blob) => {
            if (blob) {
              const thumbFile = new File([blob], `thumbnail_${Date.now()}.jpg`, { type: 'image/jpeg' });
              cleanup();
              resolve(thumbFile);
            } else {
              cleanup();
              reject(new Error('No se pudo generar el thumbnail'));
            }
          }, 'image/jpeg', 0.90);
        } catch (error) {
          cleanup();
          reject(error);
        }
      });
    });
  };

  video.onerror = () => {
    cleanup();
    reject(new Error('Error al cargar el video para generar thumbnail'));
  };

  videoUrl = URL.createObjectURL(file);
  video.src = videoUrl;

  // Timeout de seguridad
  setTimeout(() => {
    cleanup();
    reject(new Error('Timeout: el video tardó demasiado en cargar'));
  }, 30000);
});
